import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API_URL } from "../lib/supabase";
import { useCountry } from "../contexts/CountryContext";
import { getCountryConfig } from "../lib/country";
import { getServiceGradient } from "../lib/constants";
import { ServiceIcon } from "../components/shared/ServiceIcon";
import { ArrowRight, Search, X, Star, MapPin, Shield, BadgeCheck } from "lucide-react";

const ServicesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { countryCode } = useCountry();
  const showActivity = getCountryConfig(countryCode).showMarketplaceActivity !== false;
  const showProviders = getCountryConfig(countryCode).showProviderDirectory !== false;
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [matchType, setMatchType] = useState('exact');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchParams, countryCode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = searchParams.get('q') || '';
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category_slug', selectedCategory);
      if (q) params.set('search', q);
      // country_code is only meaningful when search is active — it scopes
      // the "matching providers" side of the response. Services themselves
      // are global taxonomy so this param is ignored on non-search calls.
      if (q) params.set('country_code', countryCode);
      const qs = params.toString();

      const [categoriesRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/categories`),
        fetch(`${API_URL}/services${qs ? `?${qs}` : ''}`)
      ]);

      setCategories(await categoriesRes.json());
      const data = await servicesRes.json();

      // Handle both response shapes: array (no search) or {services, providers, match_type} (search)
      if (Array.isArray(data)) {
        setServices(data);
        setProviders([]);
        setMatchType('exact');
      } else {
        setServices(data.services || []);
        setProviders(data.providers || []);
        setMatchType(data.match_type || 'exact');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const activeSearch = searchParams.get('q') || '';
  const hasResults = services.length > 0 || (showProviders && providers.length > 0);

  return (
    <div className="page services-page">
      <div className="page-header">
        <h1 data-testid="search-heading">
          {activeSearch
            ? (matchType === 'recommended'
              ? `Recommended results for "${activeSearch}"`
              : `Results for "${activeSearch}"`)
            : 'All Services'}
        </h1>
        {!activeSearch && (
          <p>Browse popular services or request a quote for any job you need done.</p>
        )}
      </div>

      {/* Search + filter bar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
        <form onSubmit={handleSearch} data-testid="services-search-form" style={{ display: 'flex', flex: '1 1 280px', minWidth: 0 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services..."
              data-testid="services-search-input"
              style={{ width: '100%', padding: '10px 36px 10px 36px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm) 0 0 var(--radius-sm)', fontSize: '0.875rem', outline: 'none' }}
            />
            {searchQuery && (
              <button type="button" onClick={clearSearch} data-testid="clear-search"
                style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px', display: 'flex' }}>
                <X size={16} />
              </button>
            )}
          </div>
          <button type="submit" className="btn-primary" data-testid="services-search-btn"
            style={{ borderRadius: '0 var(--radius-sm) var(--radius-sm) 0', padding: '10px 18px', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            Search
          </button>
        </form>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="filter-select"
          data-testid="category-filter"
          style={{ padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem', background: 'var(--card-bg)' }}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.slug}>{cat.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : hasResults ? (
        <>
          {/* Matching providers */}
          {showProviders && providers.length > 0 && (
            <div style={{ marginBottom: '28px' }} data-testid="matching-providers">
              <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '12px' }}>
                Matching Providers
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                {providers.map(prov => {
                  const isVerified = prov.is_id_verified;
                  return (
                    <Link key={prov.id} to={`/providers/${prov.id}`} data-testid={`provider-result-${prov.id}`}
                      style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px 20px', textDecoration: 'none', transition: 'box-shadow 0.2s', display: 'block' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-dark)' }}>{prov.business_name}</span>
                        {isVerified && <BadgeCheck size={15} strokeWidth={2.5} color="#047857" />}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '6px', flexWrap: 'wrap' }}>
                        {showActivity && prov.average_rating && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Star size={12} fill="#f59e0b" stroke="#f59e0b" /> {prov.average_rating} ({prov.review_count || 0})
                          </span>
                        )}
                        {prov.city && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><MapPin size={12} /> {prov.city}</span>}
                        {prov.years_experience > 0 && <span>{prov.years_experience}yr exp</span>}
                      </div>
                      {prov.bio && <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{prov.bio}</p>}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Services grid */}
          {services.length > 0 && (
            <>
              {showProviders && providers.length > 0 && (
                <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '12px' }}>
                  Services
                </h2>
              )}
              <div className="services-grid" data-testid="all-services">
                {services.map((service) => (
                  <div key={service.id} className="service-card" data-testid={`service-card-${service.slug}`}>
                    <Link to={`/services/${service.slug}`} className="service-card-link">
                      <div className={`svc-card-header ${getServiceGradient(service)}`}>
                        <div className="svc-icon-glow">
                          <ServiceIcon slug={service.slug} size={30} />
                        </div>
                        {service.category && (
                          <span className="svc-card-category">{service.category.name}</span>
                        )}
                      </div>
                      <div className="service-content">
                        <h3>{service.name}</h3>
                        <p>{service.short_description}</p>
                        <div className="service-price">{service.price_display}</div>
                      </div>
                    </Link>
                    <div className="service-card-cta">
                      <Link to={`/quote-request/${service.slug}`} className="btn-quote" data-testid={`quote-${service.slug}`}>
                        Request a Quote <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div data-testid="no-results" style={{ textAlign: 'center', padding: '48px 24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
          <p style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>No matching services found</p>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
            {activeSearch ? `We couldn't find services matching "${activeSearch}".` : 'No services available in this category.'} Try a different search or browse all services.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={clearSearch} className="btn-primary" data-testid="browse-all-btn">Browse All Services</button>
            <Link to="/quote-request/other" className="btn-outline" data-testid="custom-quote-btn">Request a Custom Quote</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
